import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Trophy, X, Download, Medal, MapPin, Calendar, Users, Filter, FileText } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getFederationLogo, imageToPngDataURL } from '../utils/federationLogos';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const EventResultsModal = ({ show, onClose, resultsData, t, language, onFilterGender }) => {
  if (!show || !resultsData) return null;

  const { session, competition, event_type, rankings = [], total_competitors, gender_filter } = resultsData;

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '-';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const formatAP = (val, eventCode) => {
    if (!val && val !== 0) return '-';
    if (eventCode === 'STA') {
      const m = Math.floor(val / 60);
      const s = Math.round(val % 60);
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${val}m`;
  };

  const getCardBadge = (card, isDq) => {
    if (isDq) return <Badge className="bg-red-700 text-white text-xs">DQ</Badge>;
    if (card === 'white') return <Badge className="bg-white text-gray-800 text-xs border border-gray-300">White</Badge>;
    if (card === 'yellow') return <Badge className="bg-yellow-500 text-gray-900 text-xs">Yellow</Badge>;
    if (card === 'red') return <Badge className="bg-red-600 text-white text-xs">Red</Badge>;
    return <Badge className="bg-gray-600 text-xs">-</Badge>;
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <span className="text-yellow-400 font-bold text-lg">1</span>;
    if (rank === 2) return <span className="text-gray-300 font-bold text-lg">2</span>;
    if (rank === 3) return <span className="text-orange-400 font-bold text-lg">3</span>;
    return <span className="text-blue-200">{rank}</span>;
  };

  const eventCode = session?.event_type_code || event_type?.code || '';

  const exportPDF = async () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const isSTA = eventCode === 'STA';
    let y = 12;

    // Try to load logos (graceful no-op if missing/blocked)
    let compLogo = null;
    let fedLogo = null;
    try {
      if (competition?.logo_url) compLogo = await imageToPngDataURL(competition.logo_url, 200);
      const fedSrc = getFederationLogo(competition?.federation);
      if (fedSrc) fedLogo = await imageToPngDataURL(fedSrc, 200);
    } catch (e) { /* ignore */ }

    // Top header band — Competition logo (left) | Title (center) | Federation logo (right)
    if (compLogo?.dataUrl) {
      try { doc.addImage(compLogo.dataUrl, 'PNG', 10, 8, 22, 22); } catch (e) {}
    }
    if (fedLogo?.dataUrl) {
      try { doc.addImage(fedLogo.dataUrl, 'PNG', 265, 8, 22, 22); } catch (e) {}
    }

    // Header: Competition
    doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 58, 95);
    doc.text(competition?.name || session?.name || 'Competition Results', 148, y, { align: 'center' });
    y += 7;

    if (competition?.federation) {
      doc.setFontSize(11); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
      doc.text(`${language === 'fr' ? 'Fédération' : 'Federation'}: ${competition.federation}`, 148, y, { align: 'center' });
      y += 5;
    }

    if (competition?.location) {
      doc.setFontSize(10); doc.setTextColor(100, 100, 100);
      doc.text(competition.location, 148, y, { align: 'center' });
      y += 5;
    }

    if (competition?.start_date) {
      const startD = new Date(competition.start_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      const endD = competition?.end_date ? new Date(competition.end_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
      doc.text(`${startD}${endD ? ' - ' + endD : ''}`, 148, y, { align: 'center' });
      y += 6;
    }

    // Make sure we're below the logos
    if (y < 36) y = 36;

    // Divider
    doc.setDrawColor(30, 58, 95); doc.setLineWidth(0.5); doc.line(10, y, 287, y); y += 5;

    // Event info
    doc.setFontSize(14); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 58, 95);
    const eventTitle = event_type?.name || eventCode || session?.event_type_code || 'Event';
    doc.text(`${language === 'fr' ? 'Resultats' : 'Results'} - ${eventTitle}`, 148, y, { align: 'center' });
    y += 5;

    doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
    doc.text(`${session?.name || ''} | ${total_competitors} ${language === 'fr' ? 'competiteurs' : 'competitors'}${gender_filter && gender_filter !== 'all' ? ` (${gender_filter === 'M' || gender_filter === 'male' ? language === 'fr' ? 'Hommes' : 'Men' : language === 'fr' ? 'Femmes' : 'Women'})` : ''}`, 148, y, { align: 'center' });
    y += 7;
    doc.setTextColor(0, 0, 0);

    // Pre-load athlete photos
    const photoMap = {};
    await Promise.all(rankings.map(async (r) => {
      if (r.photo_url) {
        const p = await imageToPngDataURL(r.photo_url, 60);
        if (p) photoMap[r.passage_id] = p;
      }
    }));

    // Build dynamic columns: photo column, plus optional avg-speed (non-STA only)
    const head = [[
      language === 'fr' ? 'Rang' : 'Rank',
      '', // photo
      language === 'fr' ? 'NOM' : 'NAME',
      language === 'fr' ? 'PRENOM' : 'FIRST',
      'S',
      'NAT',
      'AP',
      'RP',
      language === 'fr' ? 'DUREE' : 'DUR.',
      ...(isSTA ? [] : [language === 'fr' ? 'V. MOY' : 'AVG SPD']),
      language === 'fr' ? 'PENALITES' : 'PENALTIES',
      language === 'fr' ? 'CARTON' : 'CARD',
      'SCORE'
    ]];

    const tableData = rankings.map((r) => {
      const rp = r.realized_performance;
      const dur = r.performance_time_seconds;
      const avgSpeed = (!isSTA && rp && dur && dur > 0) ? `${(rp / dur).toFixed(2)} m/s` : '-';
      const row = [
        r.rank,
        '', // photo placeholder
        (r.last_name || '').toUpperCase(),
        r.first_name || '',
        r.gender === 'male' ? 'M' : r.gender === 'female' ? 'F' : '-',
        r.nation || '-',
        formatAP(r.announced_performance, eventCode),
        rp != null ? (eventCode === 'STA' ? formatDuration(rp) : `${rp}m`) : '-',
        formatDuration(dur),
        ...(isSTA ? [] : [avgSpeed]),
        (r.penalties || []).join(', ') || '-',
        r.is_dq ? 'DQ' : (r.card_result || '-'),
        r.is_dq ? '0' : (r.final_score != null ? r.final_score.toFixed(2) : '-')
      ];
      row._passageId = r.passage_id;
      return row;
    });

    const baseCol = {
      0: { cellWidth: 11, fontStyle: 'bold' },
      1: { cellWidth: 12 },                                 // photo
      2: { cellWidth: 28, halign: 'left', fontStyle: 'bold' },
      3: { cellWidth: 23, halign: 'left' },
      4: { cellWidth: 7 },
      5: { cellWidth: 13 },
      6: { cellWidth: 17 },
      7: { cellWidth: 17 },
      8: { cellWidth: 17 },
    };
    const tail = isSTA ? {
      9: { cellWidth: 38, fontSize: 7, halign: 'left' },
      10: { cellWidth: 16 },
      11: { cellWidth: 17, fontStyle: 'bold' },
    } : {
      9: { cellWidth: 18 },
      10: { cellWidth: 32, fontSize: 7, halign: 'left' },
      11: { cellWidth: 16 },
      12: { cellWidth: 17, fontStyle: 'bold' },
    };
    const columnStyles = { ...baseCol, ...tail };

    autoTable(doc, {
      startY: y,
      head,
      body: tableData,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 1.5, halign: 'center', valign: 'middle', minCellHeight: 11 },
      headStyles: { fillColor: [30, 58, 95], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      alternateRowStyles: { fillColor: [240, 245, 255] },
      columnStyles,
      margin: { left: 5, right: 5 },
      didParseCell: (data) => {
        if (data.section === 'body') {
          // Highlight podium
          if (data.row.index === 0) data.cell.styles.fillColor = [255, 245, 200];
          if (data.row.index === 1) data.cell.styles.fillColor = [230, 235, 245];
          if (data.row.index === 2) data.cell.styles.fillColor = [255, 235, 215];
          // Card column position depends on STA
          const cardIdx = isSTA ? 10 : 11;
          if (data.column.index === cardIdx) {
            const val = data.cell.raw;
            if (val === 'DQ' || val === 'red') { data.cell.styles.textColor = [220, 40, 40]; data.cell.styles.fontStyle = 'bold'; }
            if (val === 'yellow') { data.cell.styles.textColor = [180, 140, 0]; }
            if (val === 'white') { data.cell.styles.textColor = [60, 120, 60]; }
          }
        }
      },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 1) {
          const passageId = (tableData[data.row.index] || {})._passageId;
          const photo = photoMap[passageId];
          if (photo?.dataUrl) {
            const padding = 0.6;
            const size = Math.min(data.cell.height, data.cell.width) - padding * 2;
            const x = data.cell.x + (data.cell.width - size) / 2;
            const cy = data.cell.y + (data.cell.height - size) / 2;
            try { doc.addImage(photo.dataUrl, 'PNG', x, cy, size, size); } catch (e) {}
          }
        }
      }
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8); doc.setTextColor(128);
      doc.text(`Page ${i}/${pageCount} - ${language === 'fr' ? 'Genere le' : 'Generated on'} ${new Date().toLocaleDateString('fr-FR')} ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} | Apnea Coach Manager`, 148, doc.internal.pageSize.height - 8, { align: 'center' });
    }

    const fileName = `Results_${(competition?.name || session?.name || 'Event').replace(/\s+/g, '_')}_${eventCode}.pdf`;
    doc.save(fileName);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="event-results-modal">
      <div className="bg-gradient-to-br from-slate-900 to-blue-950 border border-blue-700/40 rounded-xl shadow-2xl w-[95vw] max-w-[1200px] max-h-[90vh] flex flex-col overflow-hidden">
        {/* Modal Header */}
        <div className="p-5 border-b border-blue-700/30 shrink-0">
          <div className="flex items-start justify-between">
            {/* Competition logo (left of info) */}
            {competition?.logo_url && (
              <img
                src={competition.logo_url}
                alt="Logo"
                className="w-16 h-16 rounded-lg object-contain bg-white/10 p-1 border border-white/20"
                data-testid="competition-logo-banner"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <div className="flex-1">
              {/* Competition info */}
              <div className="flex items-center gap-3 mb-2">
                <Trophy size={24} className="text-yellow-400" />
                <h2 className="text-xl font-bold text-white">{competition?.name || session?.name || t('eventResults') || 'Event Results'}</h2>
                {competition?.federation && (
                  <Badge className="bg-indigo-700 text-indigo-100">{competition.federation}</Badge>
                )}
              </div>
              {/* Competition details */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-blue-300">
                {competition?.location && (
                  <span className="flex items-center gap-1"><MapPin size={14} className="text-teal-400" />{competition.location}</span>
                )}
                {competition?.start_date && (
                  <span className="flex items-center gap-1">
                    <Calendar size={14} className="text-teal-400" />
                    {new Date(competition.start_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    {competition?.end_date && ` - ${new Date(competition.end_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`}
                  </span>
                )}
                {competition?.pool_type && (
                  <span className="text-blue-400">{competition.pool_type}{competition.pool_length ? ` ${competition.pool_length}m` : ''}</span>
                )}
              </div>
              {/* Event subdivision */}
              <div className="mt-3 bg-blue-900/40 border border-blue-700/30 rounded-lg px-4 py-2">
                <div className="flex items-center gap-3">
                  <Medal size={18} className="text-yellow-400" />
                  <span className="text-white font-semibold text-base">
                    {event_type?.name || eventCode} {session?.name ? `- ${session.name}` : ''}
                  </span>
                  <Badge className="bg-blue-700/50 text-blue-200 ml-auto">
                    <Users size={12} className="mr-1" />{total_competitors} {t('participants') || 'participants'}
                  </Badge>
                </div>
                {event_type?.description && (
                  <p className="text-blue-400 text-xs mt-1 ml-7">{event_type.description}</p>
                )}
              </div>
            </div>
            <Button onClick={onClose} variant="ghost" size="sm" className="text-blue-300 hover:text-white hover:bg-blue-800/50 ml-4" data-testid="close-results-modal">
              <X size={20} />
            </Button>
          </div>
          {/* Controls: Gender filter + PDF Export */}
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-blue-400" />
              {['all', 'male', 'female'].map(g => (
                <Button key={g} size="sm" variant={(!gender_filter || gender_filter === 'all') && g === 'all' || gender_filter === g ? 'default' : 'outline'}
                  className={(!gender_filter || gender_filter === 'all') && g === 'all' || gender_filter === g
                    ? 'bg-blue-600 text-white' : 'border-blue-600 text-blue-300 hover:bg-blue-800'}
                  onClick={() => onFilterGender && onFilterGender(g)}
                  data-testid={`filter-gender-${g}`}
                >
                  {g === 'all' ? (t('all') || 'All') : g === 'male' ? (t('male') || 'Men') : (t('female') || 'Women')}
                </Button>
              ))}
            </div>
            <Button onClick={exportPDF} className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white" size="sm" data-testid="export-results-pdf">
              <Download size={14} className="mr-1.5" />
              {t('downloadPDF') || 'Download PDF'}
            </Button>
          </div>
        </div>

        {/* Results Table (scrollable) */}
        <div className="flex-1 overflow-y-auto p-4">
          {rankings.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-blue-700/50">
                  <TableHead className="text-blue-300 text-xs w-14">{t('rank') || 'Rank'}</TableHead>
                  <TableHead className="text-blue-300 text-xs w-10"></TableHead>
                  <TableHead className="text-blue-300 text-xs">{t('lastName') || 'Last Name'}</TableHead>
                  <TableHead className="text-blue-300 text-xs">{t('firstName') || 'First Name'}</TableHead>
                  <TableHead className="text-blue-300 text-xs w-10">{t('sex') || 'S'}</TableHead>
                  <TableHead className="text-blue-300 text-xs w-14">{t('nationality') || 'Nat.'}</TableHead>
                  <TableHead className="text-blue-300 text-xs w-16">AP</TableHead>
                  <TableHead className="text-blue-300 text-xs w-16">RP</TableHead>
                  <TableHead className="text-blue-300 text-xs w-16">{t('duration') || 'Dur.'}</TableHead>
                  {eventCode !== 'STA' && (
                    <TableHead className="text-blue-300 text-xs w-20">{t('avgSpeed') || 'Avg. Speed'}</TableHead>
                  )}
                  <TableHead className="text-blue-300 text-xs">{t('penalties') || 'Penalties'}</TableHead>
                  <TableHead className="text-blue-300 text-xs w-16">{t('card') || 'Card'}</TableHead>
                  <TableHead className="text-blue-300 text-xs w-20 font-bold">{t('score') || 'Score'}</TableHead>
                  <TableHead className="text-blue-300 text-xs w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rankings.map((r) => {
                  const avgSpeed = (eventCode !== 'STA' && r.realized_performance && r.performance_time_seconds && r.performance_time_seconds > 0)
                    ? `${(r.realized_performance / r.performance_time_seconds).toFixed(2)} m/s`
                    : '-';
                  return (
                  <TableRow key={r.passage_id} className={`border-blue-700/20 ${
                    r.rank === 1 ? 'bg-yellow-900/20' : r.rank === 2 ? 'bg-gray-700/10' : r.rank === 3 ? 'bg-orange-900/15' : ''
                  }`}>
                    <TableCell className="font-bold text-center">{getRankIcon(r.rank)}</TableCell>
                    <TableCell className="text-center p-1">
                      {r.photo_url ? (
                        <img
                          src={r.photo_url}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover mx-auto border border-blue-600/30"
                          data-testid={`row-photo-${r.passage_id}`}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-blue-700/40 flex items-center justify-center text-blue-200 text-xs font-bold mx-auto">
                          {(r.first_name || '?').charAt(0)}{(r.last_name || '').charAt(0)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-white font-semibold text-sm">{(r.last_name || '').toUpperCase()}</TableCell>
                    <TableCell className="text-blue-100 text-sm">{r.first_name || '-'}</TableCell>
                    <TableCell className="text-blue-200 text-xs text-center">{r.gender === 'male' ? 'M' : r.gender === 'female' ? 'F' : '-'}</TableCell>
                    <TableCell className="text-blue-200 text-xs text-center">{r.nation || '-'}</TableCell>
                    <TableCell className="text-teal-300 text-sm text-center font-mono">{formatAP(r.announced_performance, eventCode)}</TableCell>
                    <TableCell className="text-green-300 text-sm text-center font-mono">
                      {r.realized_performance != null ? (eventCode === 'STA' ? formatDuration(r.realized_performance) : `${r.realized_performance}m`) : '-'}
                    </TableCell>
                    <TableCell className="text-blue-200 text-sm text-center font-mono">{formatDuration(r.performance_time_seconds)}</TableCell>
                    {eventCode !== 'STA' && (
                      <TableCell className="text-cyan-300 text-sm text-center font-mono" data-testid={`avg-speed-${r.passage_id}`}>{avgSpeed}</TableCell>
                    )}
                    <TableCell className="text-xs text-orange-300">
                      {(r.penalties || []).length > 0 ? r.penalties.join(', ') : '-'}
                    </TableCell>
                    <TableCell className="text-center">{getCardBadge(r.card_result, r.is_dq)}</TableCell>
                    <TableCell className={`text-center font-bold text-base font-mono ${r.is_dq ? 'text-red-400' : 'text-yellow-300'}`}>
                      {r.is_dq ? '0.00' : (r.final_score != null ? r.final_score.toFixed(2) : '-')}
                    </TableCell>
                    <TableCell className="text-center">
                      <button
                        onClick={() => {
                          const sid = session?.id || resultsData?.session_id;
                          if (!sid || !r.passage_id) return;
                          window.location.href = `${API_BASE_URL}/api/event-sessions/${sid}/athlete-pdf/${r.passage_id}`;
                        }}
                        className="inline-flex items-center justify-center w-7 h-7 rounded text-orange-300 hover:text-white hover:bg-orange-600/40 transition-colors"
                        title={t('downloadAthletePDF') || 'Download athlete report'}
                        data-testid={`download-athlete-pdf-${r.passage_id}`}
                      >
                        <FileText size={14} />
                      </button>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-blue-400">
              <Trophy size={48} className="mx-auto mb-4 opacity-40" />
              <p>{t('noResultsAvailable') || 'No results available for this event.'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventResultsModal;
